import type { GcVariety, MergedCrop } from 'interfaces/IGlobalCatalogue';
import type { ICrop, IInput } from 'interfaces/IInput';
import {
  InputStepType,
  type IInputStep,
  type IInputStepSeed,
} from 'interfaces/IInputStep';
import {
  InputTaskCategory,
  InputTaskType,
  type IInputTask,
} from 'interfaces/IInputTask';
import type { PartialUserProfile } from 'interfaces/IUserProfile';

import {
  buildStepFromSelection,
  toStepSelection,
  type ProductionCropContext,
  type StepSelection,
} from './stepFactory';
import { STEP_SELECTOR_GROUP_DATA } from './stepOptions';
import { buildTask } from './taskFactory';

// Plan defaults from the plan's crop (OP-2536): a new crop establishment task
// starts with the plan's crop and variety, the two are kept in step by an
// alert with a one-click update, and a new plan starts with a crop
// establishment task and a production task.

export const isSeedStep = (step: IInputStep): step is IInputStepSeed =>
  step.stepType === InputStepType.Seed;

const sameName = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Same catalogue item by id when both have one, otherwise by name. */
const sameItem = (
  aId: string | null | undefined,
  aName: string | null | undefined,
  bId: string | null | undefined,
  bName: string | null | undefined
) => (aId && bId ? aId === bId : sameName(aName, bName));

/**
 * True when a Seed step is sown with the plan's crop, and its variety when
 * both name one. A Seed step with no variety, or a plan with none, isn't out
 * of step on variety — one is just less specific. Shared with the Rotation
 * view (OP-2501), so both flag the same plans.
 */
export const seedMatchesPlanCrop = (
  seed: IInputStepSeed,
  crop: ICrop | null | undefined
): boolean => {
  if (!crop?.cropName) return true;
  if (!sameItem(seed.globalId, seed.item, crop.globalId, crop.cropName)) {
    return false;
  }
  if (!seed.item2 || !crop.variety?.varietyName) return true;
  return sameItem(
    seed.globalId2,
    seed.item2,
    crop.variety.globalId,
    crop.variety.varietyName
  );
};

const cropEstablishmentTasks = (input: IInput): IInputTask[] =>
  (input.tasks ?? []).filter(
    (task) => task.taskType === InputTaskType.CropEstablishment
  );

/** A crop establishment Seed step out of step with the plan's crop or variety. */
export interface PlanCropMismatch {
  /** The Seed step's crop and variety. */
  seedCrop: ICrop;
  /** True when the record is Actual — shown, never updated. */
  isActual: boolean;
  /**
   * True when the plan can take the seed's crop in one click: the task sows
   * one seed (not a mix). The plan is all that changes, so this holds for
   * Actual records and plans with several crop establishment tasks too.
   */
  canUseSeedCrop: boolean;
  /**
   * The one Plan crop establishment task and its one Seed step, when the
   * task can be updated to the plan's crop in one click; null otherwise
   * (several crop establishment tasks, a seed mix or an Actual record).
   */
  update: { task: IInputTask; seed: IInputStepSeed } | null;
}

/**
 * The plan's crop establishment mismatch, Plan records first, or null when
 * every Seed step matches the plan's crop. The plan can always take the
 * seed's crop (one seed, not a mix); the task is only updated to the plan's
 * crop when it's the plan's single crop establishment task, a Plan record
 * and sows one seed — never an Actual record.
 */
export const findPlanCropMismatch = (
  input: IInput | null | undefined
): PlanCropMismatch | null => {
  if (!input?.crop?.cropName) return null;
  const tasks = cropEstablishmentTasks(input);
  const ordered = [
    ...tasks.filter((task) => task.category !== InputTaskCategory.Actual),
    ...tasks.filter((task) => task.category === InputTaskCategory.Actual),
  ];
  for (const task of ordered) {
    const seeds = (task.steps ?? []).filter(isSeedStep);
    const seed = seeds.find((step) => !seedMatchesPlanCrop(step, input.crop));
    if (!seed) continue;
    const isActual = task.category === InputTaskCategory.Actual;
    const canUpdate = !isActual && tasks.length === 1 && seeds.length === 1;
    return {
      seedCrop: planCropFromSeed(seed),
      isActual,
      canUseSeedCrop: seeds.length === 1,
      update: canUpdate ? { task, seed } : null,
    };
  }
  return null;
};

/** The plan crop a Seed step is sown with. */
export const planCropFromSeed = (seed: IInputStepSeed): ICrop => ({
  cropName: seed.item,
  globalId: seed.globalId ?? null,
  variety: seed.item2
    ? { varietyName: seed.item2, globalId: seed.globalId2 ?? null }
    : null,
});

/** The plan's variety as its catalogue entry, or null. */
const findVariety = (
  crop: ICrop,
  cropOption: MergedCrop | null | undefined
): GcVariety | null => {
  const variety = crop.variety;
  if (!variety || !cropOption) return null;
  return (
    cropOption.varieties.find((option) =>
      sameItem(
        option.varietyId,
        option.varietyName,
        variety.globalId,
        variety.varietyName
      )
    ) ?? null
  );
};

/** The catalogue crop, when it's the plan's. */
const matchingCropOption = (
  crop: ICrop,
  cropOption: MergedCrop | null | undefined
): MergedCrop | null =>
  cropOption &&
  sameItem(cropOption.globalId, cropOption.name, crop.globalId, crop.cropName)
    ? cropOption
    : null;

/**
 * A Seed step's crop and variety set to the plan's. Its rate, unit and cost
 * stay — they belong to the crop establishment task (OP-2536 AC 5).
 */
export const seedWithPlanCrop = (
  seed: IInputStepSeed,
  crop: ICrop,
  cropOption?: MergedCrop | null
): IInputStepSeed => {
  const variety = findVariety(crop, matchingCropOption(crop, cropOption));
  return {
    ...seed,
    item: crop.cropName,
    globalId: crop.globalId ?? null,
    item2: crop.variety?.varietyName || null,
    globalId2: crop.variety?.globalId ?? null,
    techStackId: variety?.techStackId ?? null,
    snapTechStack: variety?.snapTechStack ?? null,
    manufacturerId: variety?.manufacturerId ?? null,
    snapManufacturer: variety?.snapManufacturer ?? null,
    supplierId: variety?.supplierId ?? null,
    snapSupplier: variety?.snapSupplier ?? null,
  };
};

/**
 * The Seed step a new crop establishment task starts with: the plan's crop
 * and variety, with the crop's catalogue rate, unit and cost. Null when the
 * plan has no crop yet.
 */
export const buildPlanCropSeedSelection = (
  crop: ICrop | null | undefined,
  cropOption?: MergedCrop | null
): StepSelection | null => {
  if (!crop?.cropName) return null;
  const option = matchingCropOption(crop, cropOption);
  const variety = findVariety(crop, option);
  return {
    stepType: InputStepType.Seed,
    item: crop.cropName,
    globalId: crop.globalId ?? null,
    item2: crop.variety?.varietyName || null,
    globalId2: crop.variety?.globalId ?? null,
    rate: option?.rate ?? null,
    unit: option?.rateUnit ?? null,
    cost: option?.cost ?? null,
    techStackId: variety?.techStackId ?? null,
    snapTechStack: variety?.snapTechStack ?? null,
    manufacturerId: variety?.manufacturerId ?? null,
    snapManufacturer: variety?.snapManufacturer ?? null,
    supplierId: variety?.supplierId ?? null,
    snapSupplier: variety?.snapSupplier ?? null,
  };
};

/** A new crop establishment task with the plan's crop as its Seed step. */
export const withPlanCropSeed = (
  task: IInputTask,
  crop: ICrop | null | undefined,
  cropOption?: MergedCrop | null,
  userProfile?: PartialUserProfile
): IInputTask => {
  if (task.taskType !== InputTaskType.CropEstablishment) return task;
  const selection = buildPlanCropSeedSelection(crop, cropOption);
  if (!selection) return task;
  return {
    ...task,
    steps: [
      ...(task.steps ?? []),
      buildStepFromSelection(task, selection, userProfile),
    ],
  };
};

/**
 * The crop establishment task a new plan starts with (OP-2536): a Plan task
 * sowing the plan's crop and variety — a crop is sown before it's harvested.
 */
export const buildDefaultCropEstablishmentTask = (
  input: IInput,
  cropOption?: MergedCrop | null,
  userProfile?: PartialUserProfile
): IInputTask =>
  withPlanCropSeed(
    buildTask(input, 'cropEstablishment', InputTaskCategory.Plan, userProfile),
    input.crop,
    cropOption,
    userProfile
  );

/** The tasks a new plan starts with: crop establishment, then production. */
export const buildDefaultPlanTasks = (
  input: IInput,
  context: ProductionCropContext,
  userProfile?: PartialUserProfile
): IInputTask[] => [
  buildDefaultCropEstablishmentTask(input, context.cropOption, userProfile),
  buildDefaultProductionTask(input, context, userProfile),
];

// The generic production type ("Grain") — the first in the picker.
const DEFAULT_PRODUCTION_OPTION = STEP_SELECTOR_GROUP_DATA.find(
  (group) => group.stepType === InputStepType.Production
)?.options[0];

/**
 * The production task a new plan starts with (OP-2536 AC 7): a Plan
 * production task whose production step carries the plan's crop and the
 * tonne unit, ready for its yield and price.
 */
export const buildDefaultProductionTask = (
  input: IInput,
  context: ProductionCropContext,
  userProfile?: PartialUserProfile
): IInputTask => {
  const task = buildTask(
    input,
    'production',
    InputTaskCategory.Plan,
    userProfile
  );
  if (!DEFAULT_PRODUCTION_OPTION) return task;
  const step = buildStepFromSelection(
    task,
    toStepSelection(DEFAULT_PRODUCTION_OPTION, InputStepType.Production),
    userProfile,
    context
  );
  return { ...task, steps: [step] };
};
